import { apiClient } from '../api/apiClient';
import type {
  Category,
  CreateCategoryRequest,
  UpdateCategoryRequest,
  ApiResponse,
  QueryParams,
} from '@/types/assessment';

class CategoryService {
  private readonly baseUrl = '/category';

  /**
   * Create a new category
   * POST /api/assessment/categories
   */
  async createCategory(categoryData: CreateCategoryRequest): Promise<ApiResponse<{ category: Category }>> {
    return apiClient.post(this.baseUrl, categoryData);
  }

  /**
   * Get all categories with pagination and filtering
   * GET /api/category?search=technical&isActive=true&page=1&limit=20&sort=-createdAt
   */
  async getCategories(params?: QueryParams): Promise<ApiResponse<{ categories: Category[], pagination: any }>> {
    const queryString = params ? new URLSearchParams(params as any).toString() : '';
    const url = queryString ? `${this.baseUrl}?${queryString}` : this.baseUrl;
    return apiClient.get(url);
  }

  /**
   * Get all active categories (for dropdowns)
   * GET /api/category?isActive=true
   */
  async getActiveCategories(): Promise<ApiResponse<{ categories: Category[], pagination: any }>> {
    return apiClient.get(`${this.baseUrl}?isActive=true`);
  }

  /**
   * Get single category by ID
   * GET /api/assessment/categories/{id}
   */
  async getCategory(id: string): Promise<ApiResponse<{ category: Category }>> {
    return apiClient.get(`${this.baseUrl}/${id}`);
  }

  /**
   * Update category
   * PUT /api/assessment/categories/{id}
   */
  async updateCategory(id: string, categoryData: UpdateCategoryRequest): Promise<ApiResponse<{ category: Category }>> {
    return apiClient.put(`${this.baseUrl}/${id}`, categoryData);
  }

  /**
   * Delete category
   * DELETE /api/assessment/categories/{id}
   */
  async deleteCategory(id: string): Promise<ApiResponse<{}>> {
    return apiClient.delete(`${this.baseUrl}/${id}`);
  }

  /**
   * Toggle category active status
   * PATCH /api/assessment/categories/{id}/toggle-status
   */
  async toggleCategoryStatus(id: string): Promise<ApiResponse<{ category: Category }>> {
    return apiClient.patch(`${this.baseUrl}/${id}/toggle-status`);
  }

  /**
   * Bulk create categories
   * POST /api/assessment/categories/bulk
   */
  async bulkCreateCategories(categories: CreateCategoryRequest[]): Promise<ApiResponse<{ categories: Category[] }>> {
    return apiClient.post(`${this.baseUrl}/bulk`, categories);
  }

  /**
   * Export categories to Excel
   * GET /api/assessment/categories/export
   */
  async exportCategories(): Promise<Blob> {
    const response = await apiClient.get(`${this.baseUrl}/export`, {
      responseType: 'blob',
      showLoader: true,
    });
    return response;
  }

  /**
   * Import categories from Excel
   * POST /api/assessment/categories/import
   */
  async importCategories(file: File): Promise<ApiResponse<{ imported: number; failed: number; errors?: string[] }>> {
    return apiClient.upload(`${this.baseUrl}/import`, file);
  }
}

export const categoryService = new CategoryService();